import { PLAN_DEFINITIONS, limitLabel } from '@flexbyt/shared';
import { createFileRoute } from '@tanstack/react-router';
import { PageHero } from '../components/ui/hero.js';
import { ManageScene } from '../components/mascot/scenes.js';
import { ButtonAnchor, ButtonLink, SiteLink } from '../components/ui/button.js';
import { Measure, Section } from '../components/ui/layout.js';
import { Eyebrow, Heading, Mark, Text } from '../components/ui/type.js';
import { ManageLibrary } from '../components/home/ManageLibrary.js';
import { PreviewStage } from '../components/widgets/PreviewStage.js';
import { WidgetShowcase } from '../components/WidgetShowcase.js';
import { DIRECT_INTEGRATIONS } from '../content/integrations.js';
import { MANAGE_FAQS } from '../content/manage.js';
import { sampleSetForSource } from '../content/sample-sets.js';
import { WIDGET_BUILDER_PATH, WIDGET_COUNT_WORD } from '../content/widgets.js';
import { track } from '../lib/analytics.js';
import { breadcrumbSchema, faqSchema, jsonLd, seo } from '../lib/seo.js';
import { startUrl } from '../lib/site.js';

/**
 * The middle third of the positioning.
 *
 * ===========================================================================
 * Why this page exists and the other two do not
 * ===========================================================================
 * The primary navigation has promised import → manage → display since the
 * navigation phase, pointing at `/product/import`, `/product/manage` and
 * `/product/display`. None of the three was built; the launch audit found the
 * entire Product menu leading to 404s.
 *
 * Import and display already had substantial pages under different addresses —
 * `/integrations` and `/widgets` — so building `/product/import` would have put
 * two pages of this site in competition for one query. The menu now points at
 * the pages that exist.
 *
 * Manage had nothing anywhere, which is the worse problem: it is the middle of
 * the three-word story the whole site is built on, and a visitor who wanted to
 * know what happens between importing and displaying had no page to read. This
 * is that page.
 *
 * ===========================================================================
 * What it may claim
 * ===========================================================================
 * Everything here is a capability traced through the API: `status` is
 * `'pending' | 'published'`, `BULK_REVIEW_ACTIONS` is publish/unpublish/delete,
 * reviews carry `tags`, widgets carry the tag ids they are limited to, and
 * `canReplyTo` allows a reply only on reviews ReviewsJet collected itself.
 * That last one is stated plainly rather than softened — a reply box on an
 * imported Google review would promise something that cannot happen.
 */
export const Route = createFileRoute('/manage-reviews')({
  head: () => {
    const { meta, links } = seo({
      title: 'Manage your reviews in one place',
      description:
        'Every review from every source in one library. Nothing appears on your website until you publish it. Tag reviews, filter what each widget shows, and keep the ones worth showing.',
      path: '/manage-reviews',
    });

    return {
      meta,
      links,
      scripts: [
        {
          type: 'application/ld+json',
          children: jsonLd(
            breadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Manage reviews', path: '/manage-reviews' },
            ])
          ),
        },
        {
          type: 'application/ld+json',
          children: jsonLd(faqSchema(MANAGE_FAQS)),
        },
      ],
    };
  },
  component: ManageReviews,
});

function ManageReviews() {
  const free = PLAN_DEFINITIONS.free.limits;

  return (
    <>
      <PageHero
        trail={[
          { name: 'Home', path: '/' },
          { name: 'Manage reviews', path: '/manage-reviews' },
        ]}
        art={<ManageScene />}
      >
        <Measure>
          <Eyebrow>Manage</Eyebrow>
          <Heading level={1} size="hero" className="mt-4">
            Nothing reaches your website <span className="whitespace-nowrap"><Mark>until you say so</Mark>.</span>
          </Heading>
          <Text size="lead" className="mt-6">
            Reviews from every source you connect arrive in one library, not on
            your site. You decide which ones are worth showing, and the ones you
            do not publish simply stay where they are.
          </Text>

          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
            <ButtonAnchor
              href={startUrl('manage-hero')}
              size="lg"
              onClick={() =>
                track('hero_cta_clicked', { surface: 'manage-hero' })
              }
            >
              Start free
            </ButtonAnchor>
            <ButtonLink to={WIDGET_BUILDER_PATH} variant="secondary" size="lg">
              Build a widget first
            </ButtonLink>
          </div>
        </Measure>
      </PageHero>

      {/* ------------------------------------------------ One library */}
      <Section aria-labelledby="library-heading">
        <Measure>
          <Heading level={2} id="library-heading">
            One library, whatever it came from.
          </Heading>
          <Text className="mt-4 max-w-prose">
            A Google review and a Trustpilot review sit in the same list, sort
            together and can appear in the same widget. There is no separate
            inbox per source to keep an eye on.
          </Text>
        </Measure>

        {/*
          The page's whole subject, working, above the fold of its own section.

          This page used to describe managing reviews for four sections and then
          show a widget at the end — which is a page about a screen nobody had
          seen. The library is the screen, and it is the same component the
          homepage runs, with `preview` on so the grid widget underneath is fed
          by the published rows alone.

          That second half is what makes this page's headline checkable rather
          than merely asserted: "nothing reaches your website until you say so"
          is a claim, and a card leaving the widget the instant you unpublish it
          is the demonstration. Both are drawn by the product's own renderer.
        */}
        <div className="mt-10">
          <ManageLibrary preview />
          <p className="mt-4 text-caption text-ink-500">
            Live, and local to this page — publish or unpublish a review, or
            select several and do it in bulk. Nothing here touches a real
            account, and reloading brings it all back.
          </p>
        </div>

        <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
          {DIRECT_INTEGRATIONS.map((entry) => (
            <li key={entry.slug}>
              <SiteLink
                to={`/integrations/${entry.slug}`}
                className="text-body-sm text-ink-600 underline decoration-ink-200 underline-offset-[3px] hover:text-ink-950"
              >
                {entry.name}
              </SiteLink>
            </li>
          ))}
        </ul>
      </Section>

      {/* ------------------------------------------------ What you can do */}
      <Section tone="raised" aria-labelledby="actions-heading">
        <Measure>
          <Heading level={2} id="actions-heading">
            What you actually do with them.
          </Heading>
        </Measure>

        <div className="mt-10 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {([
            {
              title: 'Publish and unpublish',
              body: 'One at a time from the row, or in bulk from the list. Unpublishing takes a review off the site without deleting it, so a decision is never final.',
            },
            {
              title: 'Tag anything',
              body: 'Tag by branch, product, service or anything else. A widget can be limited to a tag, so the reviews on your pricing page differ from the ones on your homepage.',
            },
            {
              title: 'Reply where you can',
              body: 'To reviews collected through ReviewsJet. A Google review has to be replied to in Google — nobody else can post into that thread, and a reply box here would be pretending otherwise.',
            },
            {
              title: 'Import a back catalogue',
              body: 'Bring older reviews in from a spreadsheet on any paid plan.',
            },
            {
              title: 'Auto-publish above a rating',
              body: 'On Advanced. New reviews at or above a rating you choose go straight to the site; everything else waits for you.',
            },
            {
              title: 'Ask for more',
              body: `Collection forms and review request emails — ${limitLabel(free.reviewRequests)} a month on the free plan — including a form that sends people to Google instead, when the public rating is worth more than the testimonial.`,
              href: '/review-forms',
            },
          ] as { title: string; body: string; href?: string }[]).map((item) => (
            <div key={item.title}>
              {item.href ? (
                <SiteLink
                  to={item.href}
                  className="text-title text-ink-950 underline decoration-ink-200 underline-offset-[3px] hover:text-accent-700"
                >
                  {item.title}
                </SiteLink>
              ) : (
                <p className="text-title text-ink-950">{item.title}</p>
              )}
              <p className="mt-2 max-w-prose text-body-sm text-ink-600">
                {item.body}
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* ------------------------------------------------ The result */}
      <Section aria-labelledby="result-heading">
        <Measure>
          <Heading level={2} id="result-heading">
            One library, any layout.
          </Heading>
          <Text className="mt-4 max-w-prose">
            The same published reviews drive every widget you install — a wall
            on your reviews page, a carousel on your homepage, a popup
            everywhere else. You moderate once, not once per widget.
          </Text>
        </Measure>

        {/*
          A masonry wall rather than the grid, deliberately.

          The library above already ends in a grid widget, and repeating it here
          would say "here is that again" where the section means "here is the
          same reviews somewhere else". `PreviewStage` comes with it so the wall
          can be opened at full width, which is the size it is actually built
          for — a page column understates every one of these layouts.
        */}
        <PreviewStage
          className="mt-10"
          title="Masonry widget"
          canvasClassName="rj-canvas rounded-xl p-4 ring-1 ring-ink-150 sm:p-8"
        >
          <WidgetShowcase
            type="masonry"
            reviews={sampleSetForSource('google').reviews.slice(0, 6)}
          />
        </PreviewStage>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
          <ButtonLink to="/widgets" size="lg">
            See the {WIDGET_COUNT_WORD} layouts
          </ButtonLink>
          <ButtonLink to="/integrations" variant="secondary" size="lg">
            Where reviews come from
          </ButtonLink>
        </div>
      </Section>

      {/* ------------------------------------------------ FAQ */}
      <Section tone="raised" aria-labelledby="faq-heading">
        <Measure>
          <Heading level={2} id="faq-heading">
            Questions about managing reviews.
          </Heading>
        </Measure>
        <dl className="mt-10 max-w-text">
          {MANAGE_FAQS.map((faq, index) => (
            <div
              key={faq.q}
              className={index > 0 ? 'mt-8 border-t border-ink-150 pt-8' : ''}
            >
              <dt className="text-title text-ink-950">{faq.q}</dt>
              <dd className="mt-2.5 text-body text-ink-600">{faq.a}</dd>
            </div>
          ))}
        </dl>
      </Section>
    </>
  );
}
